import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, afterNextRender, Output, Input, EventEmitter, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule, CardModule, AlertModule, SpinnerModule } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslationModule, TranslateService } from 'i18n';
import { CurrencyAmountPipe, Logger } from 'shared';
import { OrderService } from '../../../../../../services/order.service';
import { trackingErrorCategory } from 'analytics';
import { OrderWizardTracking, confirmationErrorCategory } from '../../../order-wizard-tracking';
import { AuthService } from 'auth';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-ewallet-confirmation',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    CardModule,
    AlertModule,
    SpinnerModule,
    IconModule,
    TranslationModule,
    CurrencyAmountPipe
  ],
  templateUrl: './ewallet-confirmation.component.html',
  styleUrls: ['./ewallet-confirmation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EwalletConfirmationComponent {
  @Input() orderId: number | null = null;
  @Input() totalPrice: number = 0;
  @Input() currency: string = 'USDT';
  @Output() onSuccess = new EventEmitter<any>();
  @Output() onBack = new EventEmitter<void>();

  protected logger = new Logger('EwalletConfirmationComponent');
  private orderService = inject(OrderService);
  private auth = inject(AuthService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);
  private tracking = inject(OrderWizardTracking, { optional: true });

  // ────────────────────────────────────────────────
  // Reactive state
  // ────────────────────────────────────────────────
  userBalance = signal<number>(0);
  remainingBalance = signal<number>(0);
  codeSent = signal<boolean>(false);
  confirmationCode = signal<string>('');
  userEmail = signal<string>('');
  expiresIn = signal<number>(600); // 10 minutes in seconds
  loading = signal<boolean>(false);
  error = signal<string>('');

  // Computed
  canConfirm = computed(() => 
    this.confirmationCode().length === 6 && 
    !this.loading()
  );

  formattedTime = computed(() => {
    const seconds = this.expiresIn();
    if (seconds <= 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  });

  codeExpired = computed(() => this.expiresIn() <= 0);

  // ────────────────────────────────────────────────
  // Effects + Initialization
  // ────────────────────────────────────────────────
  constructor() {
    // Set user email once
    const user = this.auth.getUser();
    this.userEmail.set(user?.email || '');

    // Auto-send code on mount (after render)
    afterNextRender(() => {
      this.sendConfirmationCode();
    });

    // Countdown effect (runs only when code is sent)
    effect(() => {
      if (!this.codeSent()) return;

      const intervalId = setInterval(() => {
        this.expiresIn.update(t => {
          if (t <= 0) {
            clearInterval(intervalId);
            this.error.set(this.translate.translate('ewallet_code_expired'));
            return 0;
          }
          return t - 1;
        });
      }, 1000);

      // Cleanup on destroy or when codeSent becomes false
      return () => clearInterval(intervalId);
    });
  }

  // ────────────────────────────────────────────────
  // Core logic
  // ────────────────────────────────────────────────
  sendConfirmationCode(): void {
    if (!this.orderId) return;

    this.loading.set(true);
    this.error.set('');

    this.orderService.initiatePayment(this.orderId, 'ewallet')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.codeSent.set(true);
          this.userBalance.set(response.user_balance ? parseFloat(String(response.user_balance)) : 0);
          this.remainingBalance.set(response.remaining_balance ? parseFloat(String(response.remaining_balance)) : 0);
          this.expiresIn.set(600); // Reset to 10 minutes
          this.loading.set(false);
        },
        error: err => {
          this.error.set(err.error?.error || this.translate.translate('ewallet_send_error'));
          this.loading.set(false);
          this.tracking?.step('payment_confirmation', 'error', confirmationErrorCategory(err));
          this.logger.error('Error sending code:', err);
        }
      });
  }

  confirmPayment(): void {
    if (!this.orderId || this.confirmationCode().length !== 6) return;

    this.tracking?.step('payment_confirmation', 'submit');
    this.loading.set(true);
    this.error.set('');

    this.orderService.confirmEwalletPayment(this.orderId, this.confirmationCode())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.loading.set(false);
          this.onSuccess.emit({
            orderId: response.order_id,
            isFirstOrder: response.is_first_order
          });
        },
        error: err => {
          this.error.set(err.error?.error || this.translate.translate('ewallet_confirm_error'));
          this.loading.set(false);
          // 422 here = wrong / expired code, i.e. a validation error, not a decline.
          this.tracking?.step('payment_confirmation', 'error', trackingErrorCategory(err));
          this.logger.error('Confirm error:', err);
        }
      });
  }

  resendCode(): void {
    this.codeSent.set(false);
    this.confirmationCode.set('');
    this.expiresIn.set(600);
    this.error.set('');
    this.sendConfirmationCode();
  }

  goBack(): void {
    this.onBack.emit();
  }
}